import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { findPreset } from './aiPresetPrompts';
import '../../styles/productAiAssistant.css';

const PAGE_SIZE = 25;

const STATUS_LABELS = {
  confirmed: { label: 'Applied', className: 'aiStatusApplied' },
  pending: { label: 'Awaiting approval', className: 'aiStatusPending' },
  rejected: { label: 'Not applied', className: 'aiStatusRejected' },
  expired: { label: 'Expired', className: 'aiStatusRejected' },
};

/**
 * "AI Activity Log" tab — every prompt this restaurant has sent the product assistant, newest
 * first, with who asked, when, and what came of it.
 *
 * Deliberately shows rejected and blocked requests too, not just applied ones. "Someone tried to
 * wipe the menu and the guardrail stopped them" is the single most useful thing this log can tell
 * you, and it only exists because refusals are recorded — so the status filter defaults to showing
 * everything rather than hiding the noise.
 */
const AiActivityLog = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async (nextOffset) => {
    setLoading(true);
    try {
      const res = await callAPI(
        apiUrls.PRODUCT_AI_LOG,
        { limit: PAGE_SIZE, offset: nextOffset },
        'GET'
      );
      const data = res.data?.data || {};
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setOffset(nextOffset);
    } catch (err) {
      console.error('Failed to load the AI activity log', err);
      toast.error('Could not load the activity log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0); }, [load]);

  const visibleRows = statusFilter === 'all'
    ? rows
    : statusFilter === '__scope'
      ? rows.filter((row) => (row.error_text || '').startsWith('Out of scope'))
      : rows.filter((row) => row.status === statusFilter);

  const formatWhen = (value) => {
    if (!value) return '—';
    // Server timestamps come back as 'YYYY-MM-DD HH:mm:ss'; Safari won't parse that with a space.
    const parsed = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="printerCard">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h5 className="aiPanelTitle mb-1">AI Activity Log</h5>
          <p className="aiPanelSubtitle mb-0">
            Every request made to the product assistant, including ones that were blocked or never
            applied.
          </p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <label className="aiFieldLabel mb-0" htmlFor="aiLogStatusFilter">Show</label>
          <select
            id="aiLogStatusFilter"
            className="form-select form-select-sm"
            style={{ width: 190 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Everything</option>
            <option value="confirmed">Applied only</option>
            <option value="rejected">Not applied</option>
            <option value="__scope">Declined — out of scope</option>
            <option value="pending">Awaiting approval</option>
            <option value="expired">Expired</option>
          </select>
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => load(offset)} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <p className="mb-0">Loading…</p>
      ) : visibleRows.length === 0 ? (
        <p className="mb-0 text-muted">
          {total === 0
            ? 'The assistant has not been used yet. Anything asked of it will be recorded here.'
            : 'No entries match this filter.'}
        </p>
      ) : (
        <div className="table-responsive">
          <table className="table aiLogTable">
            <thead>
              <tr>
                <th style={{ width: 165 }}>When</th>
                <th style={{ width: 140 }}>Requested by</th>
                <th>Prompt</th>
                <th style={{ width: 150 }}>Outcome</th>
                <th style={{ width: 140 }}>Authorised by</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const isOutOfScope = (row.error_text || '').startsWith('Out of scope');
                const status = isOutOfScope
                  ? { label: 'Declined — out of scope', className: 'aiStatusOutOfScope' }
                  : (STATUS_LABELS[row.status] || { label: row.status, className: '' });
                const preset = row.preset_key ? findPreset(row.preset_key) : null;
                const isOpen = expandedId === row.id;

                return (
                  <React.Fragment key={row.id}>
                    <tr>
                      <td className="aiLogWhen">{formatWhen(row.created_at)}</td>
                      <td>{row.requested_by || <span className="aiFieldHint">Not signed in</span>}</td>
                      <td>
                        <div className="aiLogPrompt">{row.prompt}</div>
                        {preset && <span className="aiPresetTag">{preset.label}</span>}
                      </td>
                      <td>
                        <span className={`aiStatusPill ${status.className}`}>{status.label}</span>
                        {row.status === 'confirmed' && row.affected_count > 0 && (
                          <div className="aiFieldHint mb-0">{row.affected_count} change{row.affected_count === 1 ? '' : 's'}</div>
                        )}
                      </td>
                      <td>{row.authorised_by || <span className="aiFieldHint">—</span>}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => setExpandedId(isOpen ? null : row.id)}
                        >
                          {isOpen ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr className="aiLogDetailRow">
                        <td colSpan={6}>
                          {row.summary && (
                            <div className="aiLogDetailBlock">
                              <span className="aiLogDetailLabel">Assistant replied</span>
                              <div>{row.summary}</div>
                            </div>
                          )}

                          {row.operations.length > 0 && (
                            <div className="aiLogDetailBlock">
                              <span className="aiLogDetailLabel">Proposed actions</span>
                              <ul className="mb-0">
                                {row.operations.map((op, i) => (
                                  <li key={i}>
                                    {op.description || op.type}
                                    {op.change_count > 0 && ` (${op.change_count} value${op.change_count === 1 ? '' : 's'})`}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {row.error_text && (
                            <div className="aiLogDetailBlock">
                              <span className="aiLogDetailLabel">
                                {isOutOfScope ? 'Why it was declined' : 'Blocked / error'}
                              </span>
                              <div className={isOutOfScope ? '' : 'aiLogError'}>{row.error_text}</div>
                            </div>
                          )}

                          {row.confirmed_at && (
                            <div className="aiLogDetailBlock">
                              <span className="aiLogDetailLabel">Applied</span>
                              <div>
                                {formatWhen(row.confirmed_at)}
                                {row.authorised_by ? ` by ${row.authorised_by}` : ''}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="d-flex justify-content-between align-items-center mt-2">
          <span className="aiFieldHint mb-0">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => load(Math.max(0, offset - PAGE_SIZE))}
              disabled={loading || offset === 0}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => load(offset + PAGE_SIZE)}
              disabled={loading || offset + PAGE_SIZE >= total}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiActivityLog;
